import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Divider,
  InputAdornment,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { eventsAPI, locationsAPI, type EventType, type City, type District, type MetroStation } from '../services/api';

interface EventFormData {
  title: string;
  type_id: number | '';
  start_datetime: string;
  city_id: number | '';
  district_id: number | '';
  metro_station_id: number | '';
  exact_address: string;
  meeting_place: string;
  description: string;
  max_participants: number;
  min_participants: number;
  reserved_seats: number;
  price: number;
  is_public: boolean;
}

const EditEventPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Состояния для справочников
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [metroStations, setMetroStations] = useState<MetroStation[]>([]);
  
  // Состояния для формы
  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    type_id: '',
    start_datetime: '',
    city_id: '',
    district_id: '',
    metro_station_id: '',
    exact_address: '',
    meeting_place: '',
    description: '',
    max_participants: 10,
    min_participants: 1,
    reserved_seats: 0,
    price: 0,
    is_public: true,
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // Загрузка данных мероприятия
  useEffect(() => {
    const loadEvent = async () => {
      try {
        setLoading(true);
        const event = await eventsAPI.getEvent(Number(id));
        
        setFormData({
          title: event.title,
          type_id: event.type_id,
          start_datetime: event.start_datetime.slice(0, 16),
          city_id: event.city_id,
          district_id: event.district_id || '',
          metro_station_id: event.metro_station_id || '',
          exact_address: event.exact_address || '',
          meeting_place: event.meeting_place || '',
          description: event.description || '',
          max_participants: event.max_participants,
          min_participants: event.min_participants,
          reserved_seats: event.reserved_seats,
          price: event.price,
          is_public: event.is_public,
        });
      } catch (err: any) {
        console.error('Failed to load event:', err);
        setSnackbar({
          open: true,
          message: err.response?.data?.detail || 'Не удалось загрузить мероприятие',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadEvent();
  }, [id]);
  
  // Загрузка справочников
  useEffect(() => {
    const loadDictionaries = async () => {
      try {
        const [types, allCities] = await Promise.all([
          locationsAPI.getEventTypes(),
          locationsAPI.getAllCities(),
        ]);
        setEventTypes(types);
        setCities(allCities);
      } catch (error) {
        console.error('Failed to load dictionaries:', error);
      }
    };
    
    loadDictionaries();
  }, []);
  
  // Загрузка районов при выборе города
  useEffect(() => {
    if (formData.city_id) {
      locationsAPI.getDistricts(Number(formData.city_id))
        .then(setDistricts)
        .catch(console.error);
    } else {
      setDistricts([]);
    }
  }, [formData.city_id]);
  
  // Загрузка метро при выборе города
  useEffect(() => {
    if (formData.city_id) {
      locationsAPI.getMetroStations(Number(formData.city_id))
        .then(setMetroStations)
        .catch(console.error);
    } else {
      setMetroStations([]);
    }
  }, [formData.city_id]);
  
  const handleChange = (field: keyof EventFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };
  
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.title || formData.title.length < 3) {
      newErrors.title = 'Название должно быть не менее 3 символов';
    }
    
    if (!formData.type_id) {
      newErrors.type_id = 'Выберите вид мероприятия';
    }
    
    if (!formData.start_datetime) {
      newErrors.start_datetime = 'Укажите дату и время';
    }
    
    if (!formData.city_id) {
      newErrors.city_id = 'Выберите город';
    }
    
    if (!formData.max_participants || formData.max_participants < 1) {
      newErrors.max_participants = 'Минимум 1 участник';
    }
    
    if (formData.min_participants > formData.max_participants) {
      newErrors.min_participants = 'Минимум не может быть больше максимума';
    }
    
    if (formData.reserved_seats > formData.max_participants) {
      newErrors.reserved_seats = 'Зарезервированные места не могут превышать максимум';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) {
      setSnackbar({ open: true, message: 'Исправьте ошибки в форме', severity: 'error' });
      return;
    }
    
    try {
      setSaving(true);
      
      const eventData = {
        title: formData.title,
        type_id: Number(formData.type_id),
        start_datetime: formData.start_datetime,
        city_id: Number(formData.city_id),
        district_id: formData.district_id ? Number(formData.district_id) : null,
        metro_station_id: formData.metro_station_id ? Number(formData.metro_station_id) : null,
        exact_address: formData.exact_address,
        meeting_place: formData.meeting_place,
        description: formData.description,
        max_participants: formData.max_participants,
        min_participants: formData.min_participants,
        reserved_seats: formData.reserved_seats,
        price: formData.price,
        is_public: formData.is_public,
      };
      
      await eventsAPI.updateEvent(Number(id), eventData);
      
      setSnackbar({ open: true, message: 'Изменения сохранены!', severity: 'success' });
      
      setTimeout(() => {
        navigate(`/events/${id}`);
      }, 1000);
      
    } catch (err: any) {
      console.error('Failed to update event:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Ошибка при сохранении изменений',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };
  
  const handleReset = () => {
    // Перезагружаем данные мероприятия
    const loadEvent = async () => {
      try {
        setLoading(true);
        const event = await eventsAPI.getEvent(Number(id));

        setFormData({
          title: event.title,
          type_id: event.type_id,
          start_datetime: event.start_datetime.slice(0, 16),
          city_id: event.city_id,
          district_id: event.district_id || '',
          metro_station_id: event.metro_station_id || '',
          exact_address: event.exact_address || '',
          meeting_place: event.meeting_place || '',
          description: event.description || '',
          max_participants: event.max_participants,
          min_participants: event.min_participants,
          reserved_seats: event.reserved_seats,
          price: event.price,
          is_public: event.is_public,
        });
      } catch (err: any) {
        console.error('Failed to load event:', err);
        setSnackbar({
          open: true,
          message: err.response?.data?.detail || 'Не удалось загрузить мероприятие',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };
    loadEvent();
  };
  
  // Минимальная дата
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  const minDateTime = now.toISOString().slice(0, 16);
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Заголовок */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/events/${id}`)} size="small">
            Назад
          </Button>
          <Typography variant="h4" component="h1" fontWeight="bold" sx={{ fontSize: { xs: '1.2rem', sm: '1.8rem' } }}>
            ✏️ Редактировать мероприятие
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ClearIcon />}
          onClick={handleReset}
          size="small"
        >
          Сбросить
        </Button>
      </Box>
      
      <Grid container spacing={3}>
        {/* Основная форма */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              🎯 Основная информация
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Название мероприятия"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  error={!!errors.title}
                  helperText={errors.title || `Символов: ${formData.title.length}`}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!!errors.type_id}>
                  <InputLabel>Вид мероприятия</InputLabel>
                  <Select
                    value={formData.type_id}
                    label="Вид мероприятия"
                    onChange={(e) => handleChange('type_id', e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Выберите вид</em>
                    </MenuItem>
                    {eventTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.icon_emoji} {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.type_id && <Typography color="error" variant="caption">{errors.type_id}</Typography>}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="datetime-local"
                  label="Дата и время начала"
                  value={formData.start_datetime}
                  onChange={(e) => handleChange('start_datetime', e.target.value)}
                  error={!!errors.start_datetime}
                  helperText={errors.start_datetime}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: minDateTime }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth required error={!!errors.city_id}>
                  <InputLabel>Город</InputLabel>
                  <Select
                    value={formData.city_id}
                    label="Город"
                    onChange={(e) => handleChange('city_id', e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Выберите город</em>
                    </MenuItem>
                    {cities.map((city) => (
                      <MenuItem key={city.id} value={city.id}>
                        {city.name} {city.region && `(${city.region})`}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.city_id && <Typography color="error" variant="caption">{errors.city_id}</Typography>}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  fullWidth
                  options={districts}
                  getOptionLabel={(option) => option.name}
                  disabled={!formData.city_id || districts.length === 0}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Район"
                      placeholder="Поиск района..."
                    />
                  )}
                  value={districts.find((d) => d.id === formData.district_id) || null}
                  onChange={(_, newValue) => {
                    handleChange('district_id', newValue?.id || '');
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  noOptionsText="В этом городе нет районов"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  fullWidth
                  options={metroStations}
                  getOptionLabel={(option) => option.name}
                  disabled={!formData.city_id || metroStations.length === 0}
                  filterOptions={(options, { inputValue }) => {
                    const lowerInput = inputValue.toLowerCase().trim();
                    if (!lowerInput) {
                      return options;
                    }
                    return options.filter(option => 
                      option.name.toLowerCase().includes(lowerInput)
                    );
                  }}
                  renderOption={(props, station, { selected }) => {
                    const { key, ...otherProps } = props;
                    return (
                      <Box 
                        component="li" 
                        key={station.id}
                        {...otherProps} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1,
                          bgcolor: selected ? 'action.selected' : 'transparent',
                        }} 
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 4,
                            bgcolor: station.line_color || '#999',
                            borderRadius: 1,
                            flexShrink: 0,
                          }}
                        />
                        <Box sx={{ overflow: 'hidden' }}>
                          <Typography variant="body2" noWrap>
                            {station.name}
                          </Typography>
                          {station.line_name && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {station.line_name}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Станция метро"
                      placeholder="Начните вводить название станции..."
                    />
                  )}
                  value={metroStations.find((s) => s.id === formData.metro_station_id) || null}
                  onChange={(_, newValue) => {
                    handleChange('metro_station_id', newValue?.id || '');
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  noOptionsText={metroStations.length === 0 ? "Сначала выберите город" : "Станция не найдена"}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Точный адрес"
                  value={formData.exact_address}
                  onChange={(e) => handleChange('exact_address', e.target.value)}
                  placeholder="Улица, дом, корпус"
                  helperText="Будет виден только участникам"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Место встречи"
                  value={formData.meeting_place}
                  onChange={(e) => handleChange('meeting_place', e.target.value)}
                  placeholder="Например: у главного входа, у фонтана"
                  helperText="Где участникам встретиться перед началом"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Paper>
          
          {/* Описание */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              📝 Описание
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <TextField
              fullWidth
              multiline
              rows={6}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Опишите ваше мероприятие..."
              inputProps={{ maxLength: 1000 }}
              helperText={`Символов: ${formData.description.length} / 1000`}
            />
          </Paper>
        </Grid>
        
        {/* Боковая панель */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              👥 Участники
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Минимум"
                  value={formData.min_participants}
                  onChange={(e) => handleChange('min_participants', Number(e.target.value))}
                  onFocus={(e) => e.target.select()}
                  error={!!errors.min_participants}
                  helperText={errors.min_participants}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Максимум"
                  value={formData.max_participants}
                  onChange={(e) => handleChange('max_participants', Number(e.target.value))}
                  onFocus={(e) => e.target.select()}
                  error={!!errors.max_participants}
                  helperText={errors.max_participants}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Зарезервировано"
                  value={formData.reserved_seats}
                  onChange={(e) => handleChange('reserved_seats', Number(e.target.value))}
                  onFocus={(e) => e.target.select()}
                  error={!!errors.reserved_seats}
                  helperText={errors.reserved_seats || 'Места для приглашенных'}
                  inputProps={{ min: 0 }}
                />
              </Grid>
            </Grid>
          </Paper>
          
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              💰 Стоимость
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <TextField
              fullWidth
              type="number"
              label="Цена участия"
              value={formData.price}
              onChange={(e) => handleChange('price', Number(e.target.value))}
              onFocus={(e) => e.target.select()}
              error={!!errors.price}
              helperText={errors.price || '0 = бесплатно'}
              InputProps={{
                endAdornment: <InputAdornment position="end">₽</InputAdornment>,
              }}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Paper>
          
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              🔐 Доступность
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_public}
                  onChange={(e) => handleChange('is_public', e.target.checked)}
                  color="primary"
                />
              }
              label={formData.is_public ? '🌐 Открытое' : '🔒 Закрытое'}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {formData.is_public
                ? 'Все желающие могут записаться автоматически'
                : 'Заявки будут отправляться организатору на подтверждение'}
            </Typography>
          </Paper>
          
          {/* Кнопка сохранения */}
          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={saving}
            sx={{ py: 2 }}
          >
            {saving ? 'Сохранение...' : '💾 Сохранить изменения'}
          </Button>
        </Grid>
      </Grid>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditEventPage;
